import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, ShieldCheck, ShieldAlert, User, Clock, Trash2, X, SlidersHorizontal } from "lucide-react";

function RoleBadge({ role }) {
  const { t } = useI18n();
  const map = {
    superadmin: "bg-horizonte-orange/15 text-horizonte-orange",
    admin: "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
    staff: "bg-secondary text-secondary-foreground",
  };
  const roleIcons = { superadmin: ShieldAlert, admin: ShieldCheck, staff: User };
  const Icon = roleIcons[role] || User;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[role]}`}>
      <Icon className="h-3 w-3" /> {t(role)}
    </span>
  );
}

export default function UsersPage() {
  const { t } = useI18n();
  const { isSuperadmin, user: me } = useAuth();
  const qc = useQueryClient();

  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const onOk = (msg) => () => { qc.invalidateQueries({ queryKey: ["users"] }); if (msg) toast.success(msg); };
  const onErr = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const approveMut = useMutation({ mutationFn: (id) => api.post(`/users/${id}/approve`), onSuccess: onOk(t("approved")), onError: onErr });
  const revokeMut = useMutation({ mutationFn: (id) => api.post(`/users/${id}/revoke`), onSuccess: onOk(), onError: onErr });
  const roleMut = useMutation({ mutationFn: ({ id, role }) => api.put(`/users/${id}/role`, { role }), onSuccess: onOk(t("save") + " ✓"), onError: onErr });
  const delMut = useMutation({ mutationFn: (id) => api.delete(`/users/${id}`), onSuccess: onOk("Deleted"), onError: onErr });

  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/companies")).data });
  const DOC_TYPES = ["statutory", "license", "contract", "litigation", "resolutions", "correspondence", "other"];
  const [permUser, setPermUser] = useState(null);
  const [permForm, setPermForm] = useState({ companies: [], doc_types: [], can_edit: false });
  const permMut = useMutation({ mutationFn: ({ id, body }) => api.put(`/users/${id}/permissions`, body), onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); setPermUser(null); toast.success(t("save") + " ✓"); }, onError: onErr });
  const openPerms = (u) => { const p = u.permissions || {}; setPermForm({ companies: p.companies || [], doc_types: p.doc_types || [], can_edit: !!p.can_edit }); setPermUser(u); };
  const toggleArr = (key, val) => setPermForm((s) => ({ ...s, [key]: s[key].includes(val) ? s[key].filter((x) => x !== val) : [...s[key], val] }));
  const permSummary = (u) => {
    const p = u.permissions || {};
    const lvl = p.can_edit ? t("editLabel") : t("viewLabel");
    const cScope = p.companies?.length ? `${p.companies.length} ${t("companies")}` : t("allAccess");
    const tScope = p.doc_types?.length ? `${p.doc_types.length} ${t("docTypes")}` : t("allAccess");
    return `${lvl} · ${cScope} · ${tScope}`;
  };

  const pending = users.filter((u) => u.status !== "active");
  const active = users.filter((u) => u.status === "active");

  const Avatar = ({ u }) => u.picture ? (
    <img src={u.picture} alt="" className="h-10 w-10 rounded-full object-cover" referrerPolicy="no-referrer" />
  ) : (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-horizonte-orange text-lg font-bold text-white">
      {(u.name || u.email).charAt(0).toUpperCase()}
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("users")}</h1>
        <p className="mt-1 text-muted-foreground">{t("usersSub")}</p>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-horizonte-orange border-t-transparent" /></div>
      ) : (
        <>
          {/* Pending */}
          <section>
            <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold">
              <Clock className="h-4 w-4 text-horizonte-orange" /> {t("pendingUsers")}
              {pending.length > 0 && <span className="rounded-full bg-horizonte-orange px-2 py-0.5 text-xs font-bold text-white">{pending.length}</span>}
            </h2>
            {pending.length === 0 ? (
              <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="no-pending">{t("noPending")}</Card>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {pending.map((u) => (
                  <Card key={u.user_id} data-testid={`pending-user-${u.user_id}`} className="flex items-center gap-3 border-l-4 border-l-horizonte-orange p-4">
                    <Avatar u={u} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{u.name || u.email}</div>
                      <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                    </div>
                    <Button size="sm" onClick={() => approveMut.mutate(u.user_id)} data-testid={`approve-${u.user_id}`} className="bg-horizonte-orange text-white hover:bg-renco-slate">
                      <Check className="mr-1 h-4 w-4" /> {t("approve")}
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </section>

          {/* Active */}
          <section>
            <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold">
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> {t("activeUsers")}
            </h2>
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="users-table">
                  <thead className="border-b bg-secondary/50">
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-3 font-semibold">{t("name")}</th>
                      <th className="px-4 py-3 font-semibold">{t("role")}</th>
                      <th className="px-4 py-3 text-right font-semibold">{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {active.map((u) => {
                      const isSelf = u.user_id === me?.user_id;
                      return (
                        <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="transition-colors hover:bg-secondary/40">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar u={u} />
                              <div className="min-w-0">
                                <div className="truncate font-medium">{u.name || u.email}{isSelf && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}</div>
                                <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {isSuperadmin && !isSelf ? (
                              <Select value={u.role} onValueChange={(role) => roleMut.mutate({ id: u.user_id, role })}>
                                <SelectTrigger className="h-8 w-44" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="staff">{t("staff")}</SelectItem>
                                  <SelectItem value="admin">{t("admin")}</SelectItem>
                                  <SelectItem value="superadmin">{t("superadmin")}</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <RoleBadge role={u.role} />
                            )}
                            {u.role === "staff" && (
                              <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`perm-summary-${u.user_id}`}>{permSummary(u)}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {isSuperadmin && !isSelf && u.role === "staff" && (
                                <Button size="sm" variant="ghost" onClick={() => openPerms(u)} data-testid={`perms-${u.user_id}`} className="text-horizonte-orange hover:bg-horizonte-orange/10">
                                  <SlidersHorizontal className="mr-1 h-4 w-4" /> {t("permissions")}
                                </Button>
                              )}
                              {!isSelf && u.role !== "superadmin" && (
                                <Button size="sm" variant="ghost" onClick={() => revokeMut.mutate(u.user_id)} data-testid={`revoke-${u.user_id}`} className="text-muted-foreground">
                                  <X className="mr-1 h-4 w-4" /> {t("revoke")}
                                </Button>
                              )}
                              {isSuperadmin && !isSelf && u.email !== "piergiorgio.evangelista@renco.it" && (
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-user-${u.user_id}`} onClick={() => { if (window.confirm(t("confirmDeleteUser"))) delMut.mutate(u.user_id); }}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>
        </>
      )}

      <Dialog open={!!permUser} onOpenChange={(o) => !o && setPermUser(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="perms-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{t("permTitle")}</DialogTitle>
            <DialogDescription>{permUser?.name || permUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">{t("accessLevel")}</div>
                <div className="text-xs text-muted-foreground">{permForm.can_edit ? t("canEditUpload") : t("viewOnly")}</div>
              </div>
              <Switch checked={permForm.can_edit} onCheckedChange={(v) => setPermForm((s) => ({ ...s, can_edit: v }))} data-testid="perm-canedit-switch" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t("scopeCompanies")}</Label>
              <div className="mt-2 max-h-48 space-y-2 overflow-y-auto rounded-lg border p-3">
                {companies.length === 0 && <p className="text-xs text-muted-foreground">—</p>}
                {companies.map((c) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm" data-testid={`perm-company-${c.id}`}>
                    <Checkbox checked={permForm.companies.includes(c.id)} onCheckedChange={() => toggleArr("companies", c.id)} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t("scopeTypes")}</Label>
              <div className="mt-2 grid grid-cols-2 gap-2 rounded-lg border p-3">
                {DOC_TYPES.map((tp) => (
                  <label key={tp} className="flex cursor-pointer items-center gap-2 text-sm" data-testid={`perm-type-${tp}`}>
                    <Checkbox checked={permForm.doc_types.includes(tp)} onCheckedChange={() => toggleArr("doc_types", tp)} />
                    {t(tp)}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermUser(null)} data-testid="perm-cancel-button">{t("cancel")}</Button>
            <Button onClick={() => permMut.mutate({ id: permUser.user_id, body: permForm })} data-testid="perm-save-button" className="bg-horizonte-orange text-white hover:bg-renco-slate">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
